import {
  collection,
  doc,
  type DocumentSnapshot,
  onSnapshot,
  query,
  type QueryConstraint,
  type QuerySnapshot,
  type Unsubscribe,
  where,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { AccessScope } from '../types/schema'

export type AccessScopeMode = 'ALL' | 'RESTRICTED' | 'HELPER'

export interface ActiveAccessScope {
  workspaceId: string
  uid: string
  mode: AccessScopeMode
  restricted: boolean
}

let activeAccessScope: ActiveAccessScope | null = null

export const setActiveAccessScope = (
  scope: { workspaceId: string; uid: string; mode?: AccessScopeMode; restricted?: boolean } | null,
): void => {
  if (!scope) {
    activeAccessScope = null
    return
  }
  const mode: AccessScopeMode = scope.mode ?? (scope.restricted ? 'RESTRICTED' : 'ALL')
  activeAccessScope = {
    workspaceId: scope.workspaceId,
    uid: scope.uid,
    mode,
    restricted: mode === 'RESTRICTED',
  }
}

export const getActiveAccessScope = (): ActiveAccessScope | null => {
  return activeAccessScope
}

export const isRestrictedIn = (workspaceId: string): boolean => {
  if (!activeAccessScope || !workspaceId) return false
  return activeAccessScope.workspaceId === workspaceId && activeAccessScope.mode === 'RESTRICTED'
}

export const isHelperIn = (workspaceId: string): boolean => {
  if (!activeAccessScope || !workspaceId) return false
  return activeAccessScope.workspaceId === workspaceId && activeAccessScope.mode === 'HELPER'
}

export const scopedReadConstraints = (workspaceId: string): QueryConstraint[] => {
  if (!activeAccessScope || !activeAccessScope.uid || activeAccessScope.workspaceId !== workspaceId) {
    return []
  }
  if (activeAccessScope.mode === 'RESTRICTED') {
    return [where('scopeRead', 'array-contains', activeAccessScope.uid)]
  }
  if (activeAccessScope.mode === 'HELPER') {
    return [where('helperRead', 'array-contains', activeAccessScope.uid)]
  }
  return []
}



export const subscribeToMyScopeMode = (
  workspaceId: string,
  uid: string,
  cb: (mode: AccessScopeMode) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  const memberDocRef = doc(firestoreDb, 'users', uid, 'workspaceMemberships', workspaceId)

  return onSnapshot(
    memberDocRef,
    (snap) => {
      const mode: AccessScopeMode =
        snap.exists() && snap.data()?.accessScopeMode === 'RESTRICTED'
          ? 'RESTRICTED'
          : 'ALL'
      cb(mode)
    },
    (error) => {
      onError?.(error)
    },
  )
}
 
export const saveMemberAccessScope = async (
  workspaceId: string,
  memberUid: string,
  accessScope: AccessScope,
): Promise<{ mode: 'ALL' | 'RESTRICTED'; grantCount: number }> => {
  const normalizedScope: AccessScope = {
    mode: accessScope.mode,
    grants: accessScope.mode === 'ALL' ? {} : (accessScope.grants ?? {}),
  }

  return callable<
    {
      workspaceId: string
      memberUid: string
      accessScope: AccessScope
    },
    {
      mode: 'ALL' | 'RESTRICTED'
      grantCount: number
    }
  >('setMemberAccessScope', {
    workspaceId,
    memberUid,
    accessScope: normalizedScope,
  })
}

export const subscribeToMemberAccessScope = (
  workspaceId: string,
  memberUid: string,
  cb: (scope: AccessScope) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  if (!workspaceId || !memberUid) {
    cb({ mode: 'ALL', grants: {} })
    return () => {}
  }

  const canonicalRef = doc(firestoreDb, 'workspace_members', `${workspaceId}_${memberUid}`)
  const membersCol = collection(firestoreDb, 'workspace_members')
  const qUid = query(
    membersCol,
    where('workspaceId', '==', workspaceId),
    where('uid', '==', memberUid),
  )
  const qUserId = query(
    membersCol,
    where('workspaceId', '==', workspaceId),
    where('userId', '==', memberUid),
  )

  const latestById = new Map<string, Record<string, unknown>>()
  let canonicalIds = new Set<string>()
  let uidIds = new Set<string>()
  let userIdIds = new Set<string>()
  let canonicalReady = false
  let uidReady = false
  let userIdReady = false

  const updateListenerDocs = (
    currentSet: Set<string>,
    newDocs: Array<{ id: string; data: Record<string, unknown> }>,
    otherSets: Array<Set<string>>,
  ): Set<string> => {
    const nextSet = new Set<string>()
    for (const d of newDocs) {
      nextSet.add(d.id)
      latestById.set(d.id, d.data)
    }
    for (const id of currentSet) {
      if (!nextSet.has(id)) {
        const retainedByOther = otherSets.some((s) => s.has(id))
        if (!retainedByOther) {
          latestById.delete(id)
        }
      }
    }
    return nextSet
  }

  const emit = (): void => {
    if (!canonicalReady || !uidReady || !userIdReady) {
      return
    }

    const activeRows = Array.from(latestById.values()).filter((data) => {
      const status = typeof data.status === 'string' ? data.status : 'ACTIVE'
      return status === 'ACTIVE'
    })

    if (activeRows.length === 0) {
      cb({ mode: 'ALL', grants: {} })
      return
    }

    const activeWithScope = activeRows.find(
      (r) => r.accessScope && typeof r.accessScope === 'object',
    )
    if (!activeWithScope || !activeWithScope.accessScope) {
      cb({ mode: 'ALL', grants: {} })
      return
    }

    const raw = activeWithScope.accessScope as AccessScope
    const mode: 'ALL' | 'RESTRICTED' = raw.mode === 'RESTRICTED' ? 'RESTRICTED' : 'ALL'
    const grants =
      mode === 'RESTRICTED' && raw.grants && typeof raw.grants === 'object'
        ? raw.grants
        : {}

    cb({ mode, grants })
  }

  const unsubCanonical = onSnapshot(
    canonicalRef,
    (snap: DocumentSnapshot) => {
      const newDocs = snap.exists()
        ? [{ id: snap.id, data: (snap.data() ?? {}) as Record<string, unknown> }]
        : []
      canonicalIds = updateListenerDocs(canonicalIds, newDocs, [uidIds, userIdIds])
      canonicalReady = true
      emit()
    },
    (err: { code?: string } & Error) => {
      // For managers, existing workspace members are always readable; permission-denied on a uid-keyed path means the doc does not exist
      if (err.code === 'permission-denied') {
        canonicalIds = updateListenerDocs(canonicalIds, [], [uidIds, userIdIds])
        canonicalReady = true
        emit()
        return
      }
      onError?.(err)
    },
  )

  const unsubUid = onSnapshot(
    qUid,
    (snap: QuerySnapshot) => {
      const newDocs = snap.docs.map((d) => ({
        id: d.id,
        data: (d.data() ?? {}) as Record<string, unknown>,
      }))
      uidIds = updateListenerDocs(uidIds, newDocs, [canonicalIds, userIdIds])
      uidReady = true
      emit()
    },
    (err) => onError?.(err),
  )

  const unsubUserId = onSnapshot(
    qUserId,
    (snap: QuerySnapshot) => {
      const newDocs = snap.docs.map((d) => ({
        id: d.id,
        data: (d.data() ?? {}) as Record<string, unknown>,
      }))
      userIdIds = updateListenerDocs(userIdIds, newDocs, [canonicalIds, uidIds])
      userIdReady = true
      emit()
    },
    (err) => onError?.(err),
  )

  return () => {
    unsubCanonical()
    unsubUid()
    unsubUserId()
  }
}

